<details *ngIf="!sceneOnly" class="viewer-panel scene-selector-panel" [open]="true">
  <summary class="viewer-panel__panel-summary">Scenes</summary>
  <div *ngIf="ingestState.error !== null" class="ingest-panel__error" role="alert">
    {{ ingestState.error }}
  </div>
  <div class="viewer-panel__content">
    <div class="viewer-panel__properties scene-selector__row">
      <select
        class="viewer-panel__select scene-selector"
        aria-label="Scene"
        [value]="selectedSceneId ?? ingestState.sceneId ?? ''"
        [disabled]="sceneModelActionPending"
        (change)="onSelectedSceneChanged($event)"
      >
        <option value="" [selected]="selectedSceneId === null && ingestState.sceneId === null">Select scene</option>
        <option
          *ngIf="selectedSceneId !== null && !isSelectedSceneListed()"
          [value]="selectedSceneId"
          [selected]="selectedSceneId === (selectedSceneId ?? ingestState.sceneId)"
        >
          {{ getSceneDisplayLabelById(selectedSceneId) }}
        </option>
        <option
          *ngFor="let scene of scenesRegistry"
          [value]="scene.id"
          [selected]="scene.id === (selectedSceneId ?? ingestState.sceneId)"
        >
          {{ getSceneDisplayName(scene) }}
        </option>
      </select>
      <!-- The toolbar carries the help link once a scene is open; phones hide the toolbar, so they keep this one. -->
      <a
        class="scene-selector__help"
        [class.scene-selector__help--phone-only]="ingestState.sceneId !== null"
        routerLink="/help"
        target="_blank"
        rel="noopener"
        title="Viewer help (opens in a new tab)"
        aria-label="Viewer help (opens in a new tab)"
        >?</a
      >
    </div>
  </div>
</details>

<nav
  *ngIf="ingestState.sceneId !== null"
  class="viewer-workspace"
  [class.viewer-workspace--structure]="activeViewerWorkspace === 'structure'"
  [class.viewer-workspace--viewpoints]="activeViewerWorkspace === 'viewpoints'"
  [class.viewer-workspace--tools]="activeViewerWorkspace === 'tools'"
  [class.viewer-workspace--settings]="activeViewerWorkspace === 'settings'"
  [class.viewer-workspace--quality]="activeViewerWorkspace === 'quality'"
  [class.viewer-workspace--performance]="activeViewerWorkspace === 'performance'"
  aria-label="Viewer workspace"
  role="tablist"
>
  <button
    type="button"
    role="tab"
    [attr.aria-selected]="activeViewerWorkspace === 'structure'"
    (click)="selectViewerWorkspace('structure')"
  >
    Structure
  </button>
  <button
    type="button"
    role="tab"
    [attr.aria-selected]="activeViewerWorkspace === 'viewpoints'"
    (click)="selectViewerWorkspace('viewpoints')"
  >
    Viewpoints
  </button>
  <button
    type="button"
    role="tab"
    [attr.aria-selected]="activeViewerWorkspace === 'quality'"
    (click)="selectViewerWorkspace('quality')"
  >
    Quality
  </button>
  <button
    type="button"
    role="tab"
    [attr.aria-selected]="activeViewerWorkspace === 'settings'"
    (click)="selectViewerWorkspace('settings')"
  >
    Settings
  </button>
  <button
    type="button"
    role="tab"
    [attr.aria-selected]="activeViewerWorkspace === 'performance'"
    (click)="selectViewerWorkspace('performance')"
  >
    FPS {{ fps }}
  </button>
  <button
    type="button"
    role="tab"
    [attr.aria-selected]="activeViewerWorkspace === 'tools'"
    (click)="selectViewerWorkspace('tools')"
  >
    Tools
  </button>
</nav>

<section
  viewer-viewpoints-panel
  [viewer]="this"
  *ngIf="ingestState.sceneId !== null"
  class="viewer-panel viewer-panel--viewpoints"
  role="tabpanel"
  aria-label="Viewpoints"
></section>

<section
  viewer-quality-panel
  [viewer]="this"
  class="runtime-settings runtime-settings--quality"
  role="tabpanel"
  aria-label="Quality settings"
></section>

<section
  viewer-settings-panel
  [viewer]="this"
  class="runtime-settings"
  role="tabpanel"
  aria-label="Viewer settings"
></section>

<div
  *ngIf="sceneOnly && (runtimeSettingsError !== null || ingestState.error !== null)"
  class="scene-only-error"
  role="alert"
>
  {{ runtimeSettingsError ?? ingestState.error }}
</div>
<div
  *ngIf="sceneOnly && selectedSceneId === null && runtimeSettingsError === null"
  class="scene-only-error"
  role="status"
>
  No scene selected.
</div>
<canvas #canvasDocument class="canvas" [class.canvas--scene-only]="sceneOnly"></canvas>
<div #labelsLayer class="viewer-labels" aria-hidden="true"></div>
<div *ngIf="touchNavigationControlsVisible" class="touch-navigation" aria-label="Camera controls">
  <button
    type="button"
    class="touch-navigation__button"
    title="Move forward"
    aria-label="Move forward"
    (pointerdown)="startTouchMove('forward', $event)"
    (pointerup)="stopTouchMove()"
    (pointercancel)="stopTouchMove()"
    (pointerleave)="stopTouchMove()"
    (lostpointercapture)="stopTouchMove()"
    (contextmenu)="$event.preventDefault()"
  >
    +
  </button>
  <button
    type="button"
    class="touch-navigation__button"
    title="Move backward"
    aria-label="Move backward"
    (pointerdown)="startTouchMove('backward', $event)"
    (pointerup)="stopTouchMove()"
    (pointercancel)="stopTouchMove()"
    (pointerleave)="stopTouchMove()"
    (lostpointercapture)="stopTouchMove()"
    (contextmenu)="$event.preventDefault()"
  >
    &minus;
  </button>
  <button
    type="button"
    class="touch-navigation__button touch-navigation__button--fit"
    title="Fit scene"
    aria-label="Fit scene"
    (click)="fitCameraFromTouchControls()"
  >
    Fit
  </button>
</div>
<nav
  viewer-toolbar
  [viewer]="this"
  *ngIf="!sceneOnly && ingestState.sceneId !== null"
  class="viewer-toolbar"
  aria-label="Viewer tools"
></nav>
<div
  viewer-loading-overlay
  [viewer]="this"
  *ngIf="viewerLoadingPanelVisible"
  class="viewer-loading"
  role="status"
  aria-live="polite"
  [attr.aria-label]="viewerLoadingMessage"
></div>
<div *ngIf="viewerAwaitingFirstGeometry" class="viewer-first-load" role="status" aria-live="polite">
  <span class="viewer-first-load__spinner" aria-hidden="true"></span>
  <span>Loading scene…</span>
</div>
<section
  viewer-tools-panel
  [viewer]="this"
  class="viewer-panel viewer-panel--inspector"
  *ngIf="ingestState.sceneId !== null"
  role="tabpanel"
  aria-label="Scene tools"
></section>

<section
  viewer-scene-tree-panel
  [viewer]="this"
  class="viewer-panel viewer-panel--scene-tree"
  *ngIf="ingestState.sceneId !== null"
  role="tabpanel"
  aria-label="Scene structure"
></section>

<section
  viewer-stats-panel
  [viewer]="this"
  *ngIf="(!sceneOnly || debugEnabled) && ingestState.sceneId !== null"
  class="viewer-panel viewer-panel--debug viewer-stats-section"
  role="tabpanel"
  aria-label="Performance debug"
></section>
